import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Loader2, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "fatheadproductions0@gmail.com";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-b border-zinc-800 focus:border-yellow-400 text-white px-0 py-4 w-full transition-colors placeholder:text-zinc-600 focus:outline-none";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-start">
        {/* Left */}
        <div className="lg:col-span-5">
          <span className="text-xs tracking-[0.3em] uppercase text-yellow-400 font-semibold">
            04 — Contact
          </span>
          <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-5xl sm:text-7xl uppercase leading-[0.9]">
            Let&rsquo;s <br />
            <span className="text-yellow-400">Talk</span>
          </h2>
          <p className="mt-8 text-zinc-400 leading-relaxed max-w-sm">
            Ready to scale? Tell us about your brand and where you want to take
            it. We reply to every serious inquiry.
          </p>

          <a
            data-testid="contact-email-link"
            href={`mailto:${EMAIL}`}
            className="group mt-8 inline-flex items-center gap-3 text-white hover:text-yellow-400 transition-colors"
          >
            <Mail size={20} className="text-yellow-400" />
            <span className="text-lg break-all">{EMAIL}</span>
            <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>

          <a
            data-testid="contact-linkedin-link"
            href="https://www.linkedin.com/in/fat-head-advertisements-863963420/"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-4 flex items-center gap-3 text-white hover:text-yellow-400 transition-colors"
          >
            <Linkedin size={20} className="text-yellow-400" />
            <span className="text-lg">Connect on LinkedIn</span>
            <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>

          <div className="mt-10 border border-white/10 max-w-xs hidden sm:block">
            <img
              src="/images/contact.png"
              alt="Character waving hello"
              className="w-full select-none"
              draggable={false}
            />
          </div>
        </div>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 space-y-8"
        >
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label className="text-xs tracking-widest text-zinc-500 uppercase">Name *</label>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs tracking-widest text-zinc-500 uppercase">Email *</label>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@brand.com"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="text-xs tracking-widest text-zinc-500 uppercase">Company</label>
            <input
              data-testid="contact-input-company"
              value={form.company}
              onChange={update("company")}
              placeholder="Your brand / company"
              className={inputCls}
            />
          </div>

          <div>
            <label className="text-xs tracking-widest text-zinc-500 uppercase">Message *</label>
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={update("message")}
              rows={5}
              placeholder="Tell us what you're building and where you want to take it..."
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="group inline-flex items-center justify-center gap-2 bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-60 px-10 py-4 text-sm uppercase tracking-widest font-bold transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending
              </>
            ) : (
              <>
                Send Message
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
